/**
 * The Initialisation reducer is intended to contain any data required to initialise the app into either
 * Visual Inspiration or Visual Configuration.
 *
 * It also contains information allowing us to distinguish between Inspiration and Conspiration, so that
 * we can know what data is needed so that we can define accurate loading states for that data.
 *
 * That data happens to be useful for other purposes, so quite a lot of stuff ends up selecting
 * this slice.
 *
 * TODO: Initialisation isn't an appropriate name given how this has grown and is currently being
 * used. Think of something better.
 */

import { Api, Fixtuur, ModularSofa } from '@fixtuur/backpack';
import * as RTK from '@reduxjs/toolkit';

export type IdleState = { state: 'idle' };
export type ErrorState = { state: 'error' };
export type InspirationState = {
    state: 'inspiration';
    shouldOpenArModal: boolean;
    productId: string;
    launchProductId: string;
    shouldDisplayUnpublished: boolean;
};

export type ConfigurationState = {
    state: 'configuration';
    shouldOpenArModal: boolean;
    /** What selections should the configuration start with */
    startWithSelections: Fixtuur.Configuration.Selections.Selections | null;
    /**
     * Represents the configurator being used to drive the Configuration 3D view.
     *
     * Also represents the configuration lifecycle: if set we are in Configuration, if unset we are not.
     */
    configuratorDefinition: Api.ConfiguratorDefinition.ConfiguratorDefinition;

    /** extracted from the configurator definition */
    configurableProperties: Fixtuur.Configuration.ConfigurableProperties.ConfigurableProperties;

    /**
     * Remember which inspiration product we came with so we can return to it
     * will probably also represent locked-in product when we come to implement that
     */
    startingProduct: InspirationState | null;
    startingGroupingCode: string | null;
    shouldDisplayUnpublished: boolean;
};

export type ModularState = {
    state: 'modular';
    shouldOpenArModal: boolean;
    configuratorId: string;
    startWithSelections: {
        pieceSelections: (string | ModularSofa.PropertySelections)[];
        globalSelections: ModularSofa.PropertySelections;
    };
};

export type State = IdleState | ErrorState | InspirationState | ConfigurationState | ModularState;

export const initialState: State = { state: 'idle' } as State;

export const slice = RTK.createSlice({
    name: 'initialisation',
    initialState,
    reducers: {
        // Load into inspiration regardless of what's currently happening
        initialiseInspiration(
            state,
            action: RTK.PayloadAction<{
                shouldOpenArModal?: boolean;
                productId: string;
                shouldDisplayUnpublished?: boolean;
            }>,
        ): State {
            const {
                shouldOpenArModal: shouldOpenArModalInput,
                productId,
                shouldDisplayUnpublished: shouldDisplayUnpublishedInput,
            } = action.payload;
            const shouldOpenArModal = shouldOpenArModalInput ?? false;
            const shouldDisplayUnpublished = shouldDisplayUnpublishedInput ?? false;

            return {
                state: 'inspiration',
                shouldOpenArModal,
                productId,
                launchProductId: productId,
                shouldDisplayUnpublished,
            };
        },

        /** Update the InspirationProduct - e.g. after resolving new selections */
        updateInspirationProduct(state, action: RTK.PayloadAction<{ productId: string }>) {
            if (state.state !== 'inspiration') {
                // TODO: logging
                console.warn('failed to update inspiration product: we are not in inspiration!');
                return;
            }

            state.productId = action.payload.productId;
        },
        // Load into configuration regardless of what's currently happening
        // or transition from inspiration to configuration (i.e. add parts journey)
        initialiseConfiguration(
            state,
            action: RTK.PayloadAction<{
                configuratorDefinition: Api.ConfiguratorDefinition.ConfiguratorDefinition;
                configurableProperties: Fixtuur.Configuration.ConfigurableProperties.ConfigurableProperties;
                /**
                 * Set to true if initialising from add parts - if true we will remember the inspiration product
                 * used to launch configuration, so that we can return to inspiration with that product if needed
                 * via the resetConfiguration action.
                 */
                rememberInspirationProduct: boolean;
                shouldOpenArModal: boolean;
                /** What selections should the configuration start with */
                startWithSelections: Fixtuur.Configuration.Selections.Selections | null;
                shouldDisplayUnpublished?: boolean;
                startingGroupingCode: string | null;
            }>,
        ): State {
            const {
                shouldOpenArModal,
                configuratorDefinition,
                configurableProperties,
                rememberInspirationProduct,
                startWithSelections,
                startingGroupingCode,
                shouldDisplayUnpublished: shouldDisplayUnpublishedInput,
            } = action.payload;

            let startingProduct = null;

            if (rememberInspirationProduct) {
                if (state.state !== 'inspiration') {
                    // TODO: logging
                    console.warn(
                        'initialised configuration from inspiration but no inspiration product found',
                    );
                } else {
                    startingProduct = state;
                }
            }

            const shouldDisplayUnpublished = shouldDisplayUnpublishedInput ?? false;

            return {
                state: 'configuration',
                shouldOpenArModal,
                configuratorDefinition,
                configurableProperties,
                startWithSelections,
                startingProduct,
                startingGroupingCode,
                shouldDisplayUnpublished,
            };
        },
        // transition back from configuration to inspiration (i.e. remove parts journey)
        resetConfiguration(state) {
            if (state.state === 'configuration' && state.startingProduct) {
                return state.startingProduct;
            }
            // TODO: This doesn't seem right
            return { state: 'idle' };
        },
        initialiseModular(
            state,
            action: RTK.PayloadAction<{
                shouldOpenArModal: boolean;
                configuratorId: string;
                startWithSelections: {
                    pieceSelections: (string | ModularSofa.PropertySelections)[];
                    globalSelections: ModularSofa.PropertySelections;
                };
            }>,
        ): State {
            const { shouldOpenArModal, configuratorId, startWithSelections } = action.payload;

            return {
                state: 'modular',
                configuratorId,
                shouldOpenArModal,
                startWithSelections,
            };
        },
        error(_state) {
            return { state: 'error' };
        },
        // Unclear what this is for, if we're in /explorer calling this isn't currently
        // handled and would lead to undefined behaviour
        clear(_state) {
            return { state: 'idle' };
        },
        resetShowArModal(state) {
            if (state.state === 'inspiration') {
                // state.inspiration.shouldOpenArModal = false;
            }
        },
    },
});

export const {
    initialiseInspiration,
    updateInspirationProduct,
    initialiseConfiguration,
    resetConfiguration,
    initialiseModular,
    error,
    clear,
    resetShowArModal,
} = slice.actions;

export const reducer = slice.reducer;
